import React from 'react';
import { Bot, Folder, PanelLeft, PlugZap, Radar, Radio, Search, Sparkles, SquarePen, Users } from 'lucide-react';

// The dashboard-icons CDN is missing marks for several of these (HubSpot,
// Snowflake, QuickBooks, Intercom), which rendered as broken images. Resolving
// through each brand's own domain is the pattern used elsewhere in the project
// and covers every tool we reference.
const TOOL_DOMAINS: Record<string, string> = {
  slack: 'slack.com',
  gmail: 'mail.google.com',
  notion: 'notion.so',
  salesforce: 'salesforce.com',
  linear: 'linear.app',
  hubspot: 'hubspot.com',
  snowflake: 'snowflake.com',
  stripe: 'stripe.com',
  quickbooks: 'quickbooks.intuit.com',
  figma: 'figma.com',
  intercom: 'intercom.com',
  ahrefs: 'ahrefs.com',
  github: 'github.com',
  sentry: 'sentry.io',
  mailchimp: 'mailchimp.com',
  granola: 'granola.ai',
  microsoftteams: 'teams.microsoft.com',
  meta: 'meta.com',
  // AI surfaces the connections are exposed to.
  giga: 'getgiga.com',
  chatgpt: 'openai.com',
  claude: 'claude.ai',
  // grok.com carries Grok's own mark; x.ai's favicon is the plain X wordmark,
  // which reads as SpaceX rather than the product.
  grok: 'grok.com'
};
export const toolLogo = (slug: string) => `https://www.google.com/s2/favicons?domain=${TOOL_DOMAINS[slug] ?? `${slug}.com`}&sz=64`;

// Favicons are 64px rasters that bake in their own white plate and their own
// internal padding, so a row of them never matches optically. Where a brand has
// a clean vector mark, prefer it: transparent, crisp at any size, and drawn in
// the brand's own colour.
// Slack asked Simple Icons to drop its mark, so that slug 404s — it has to
// keep resolving through the favicon.
const BRAND_MARKS: Record<string, string> = {
  salesforce: 'salesforce/00A1E0',
  notion: 'notion/2B211A',
  linear: 'linear/5E6AD2',
  jira: 'jira/0052CC',
  hubspot: 'hubspot/FF7A59',
  intercom: 'intercom/1F8DED',
  github: 'github/2B211A',
  figma: 'figma/F24E1E',
  stripe: 'stripe/635BFF',
  googledrive: 'googledrive/1A73E8',
  googlesheets: 'googlesheets/34A853',
  gmail: 'gmail/EA4335',
  zoom: 'zoom/0B5CFF',
  asana: 'asana/F06A6A',
  confluence: 'confluence/172B4D',
  meta: 'meta/0866FF',
  // AI surfaces. Their favicons bake in a white plate, so the transparent
  // vector matters more here than anywhere — but Simple Icons can drop or
  // rename either slug, hence the runtime fallback in BrandLogo.
  chatgpt: 'openai/2B211A',
  claude: 'claude/D97757'
};

/**
 * Giga's own mark is not on any icon CDN. This is the same Vector.png the site
 * header uses — the product screens were pointing at a different, older export
 * of the mark, which is why Giga read as slightly wrong beside real brand logos.
 */
const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png';

/** Crisp vector mark where one exists, otherwise the brand's favicon. */
export const brandMark = (slug: string) => slug === 'giga' ? GIGA_MARK : BRAND_MARKS[slug] ? `https://cdn.simpleicons.org/${BRAND_MARKS[slug]}` : toolLogo(slug);

// Optical sizing. Every mark is given the same box, but brands bake wildly
// different padding into their own artwork — OpenAI's knot in particular sits
// small inside a lot of empty space, so at an identical box it reads a size
// smaller than Slack or Notion beside it. These multipliers correct the drawn
// mark to the same visual weight; the box itself never changes.
const LOGO_SCALE: Record<string, number> = {
  chatgpt: 1.3,
  claude: 1.1,
  granola: 1.1
};

/**
 * Simple Icons drops marks whenever a brand asks it to, so any slug in the map
 * can start 404ing without warning. Rendering through a component lets a failed
 * vector fall back to the favicon at runtime instead of leaving a broken image,
 * and gives every logo one place to be optically sized.
 */
export function BrandLogo({
  slug,
  className = ''
}: {
  slug: string;
  className?: string;
}) {
  const [src, setSrc] = React.useState(() => brandMark(slug));
  React.useEffect(() => setSrc(brandMark(slug)), [slug]);
  const scale = LOGO_SCALE[slug];
  return <img src={src} alt="" aria-hidden="true" onError={() => setSrc(toolLogo(slug))} className={`${className} object-contain`} style={scale ? {
    transform: `scale(${scale})`
  } : undefined} data-id="element-193" data-mp="2v30fp7e8ikm" />;
}

/**
 * Shared chrome for every real-product screenshot on the page. One frame means
 * Connections, the platform primitives and the brain all read as the same
 * application rather than four unrelated illustrations.
 */
export function AppChrome({
  children,
  sidebar = true,
  active,
  className = ''
}: {
  children: React.ReactNode;
  sidebar?: boolean;
  active?: string;
  className?: string;
}) {
  // No browser bar: the fake URL and window chrome added a frame around the
  // frame without telling anyone anything they didn't already know.
  // Two-part shadow: a 1px contact edge keeps the frame crisp against cream,
  // while the long soft cast gives it real elevation rather than a flat drop.
  return <div className={`overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.05),0_8px_24px_-12px_rgba(43,33,26,0.18),0_40px_100px_-40px_rgba(43,33,26,0.45)] ${className}`} data-id="element-194" data-mp="14pnx679fc4kw">
      <div className="flex min-h-[420px]" data-id="element-195" data-mp="vqkfn7pwnx8i">
        {sidebar && <AppSidebar active={active} data-id="element-196" data-mp="vnoj9ntphgm6" />}
        <div className="min-w-0 flex-1 bg-white" data-id="element-197" data-mp="1sg1qs27nqbhl">{children}</div>
      </div>
    </div>;
}
const SIDEBAR_CHATS = ['Inbox catch-up', 'Renewal risk', 'Board deck notes'];
function AppSidebar({
  active
}: {
  active?: string;
}) {
  return <aside className="hidden w-[196px] shrink-0 flex-col border-r border-[#EFEAE1] bg-[#FCFAF6] px-2.5 py-3 lg:flex" data-id="element-198" data-mp="1jd91u5ly6bux">
      <div className="mb-3 flex items-center justify-between px-1.5" data-id="element-199" data-mp="1eid72xpbtae4">
        <span className="flex items-center gap-2" data-id="element-200" data-mp="81hxzzv9u4e0">
          <span className="flex size-6 items-center justify-center rounded-lg bg-[#134E4A] text-[10px] font-bold text-white" data-id="element-201" data-mp="313t2qoyvak7">
            BW
          </span>
          <span className="text-[12.5px] font-semibold text-[#2B211A]" data-id="element-202" data-mp="4isjt4ns2z8g">
            Brightwave
          </span>
        </span>
        <PanelLeft size={13} className="text-neutral-300" data-id="element-203" data-mp="3rmp910nje0i0" />
      </div>
      <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#E7E0D4] bg-white px-2.5 py-1.5" data-id="element-204" data-mp="3rmfldjpys5fh">
        <SquarePen size={12} className="text-neutral-400" data-id="element-205" data-mp="iie7t3weau4" />
        <span className="text-[12px] font-semibold text-[#2B211A]" data-id="element-206" data-mp="3hw42mr1mfroj">
          New chat
        </span>
      </div>
      <div className="mb-1 flex items-center gap-2 rounded-lg bg-white px-2 py-1.5 ring-1 ring-[#EFEAE1]" data-id="element-207" data-mp="3mp6xd2i4x76b">
        <Search size={11} className="text-neutral-300" data-id="element-208" data-mp="38y27gs4kjt96" />
        <span className="text-[11.5px] text-neutral-400" data-id="element-209" data-mp="3cx18w2r3d7vv">Search</span>
      </div>
      <SidebarGroup label="Manage" items={MANAGE_ITEMS} active={active} data-id="element-210" data-mp="2yybbsn49v87i" />
      <SidebarGroup label="Brain" items={BRAIN_ITEMS} active={active} data-id="element-211" data-mp="2z04gvxz8lkde" />
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-212" data-mp="33wl6yxpzy8t4">
        Chats
      </p>
      <ul data-id="element-213" data-mp="2kd5vajn29k6l">
        {SIDEBAR_CHATS.map(c => <li key={c} className="truncate rounded-lg px-2 py-[5px] text-[12px] font-medium text-neutral-500" data-id="element-214" data-mp="2fcro13taae9m">
            {c}
          </li>)}
      </ul>
    </aside>;
}
const BRAIN_ITEMS = [{
  label: 'Tracks',
  icon: Folder
}, {
  label: 'Scans',
  icon: Radar
}, {
  label: 'Observations',
  icon: Sparkles
}];
const MANAGE_ITEMS = [{
  label: 'Assistants',
  icon: Bot
}, {
  label: 'Pulses',
  icon: Radio
}, {
  label: 'Connections',
  icon: PlugZap
}, {
  label: 'Groups',
  icon: Users
}];
function SidebarGroup({
  label,
  items,
  active
}: {
  label: string;
  items: Array<{
    label: string;
    icon: React.ElementType;
  }>;
  active?: string;
}) {
  return <>
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-215" data-mp="3m0ohpcvzytgl">
        {label}
      </p>
      <ul data-id="element-216" data-mp="3hzw8gjn4kokb">
        {items.map(item => {
        const isActive = active === item.label;
        return <li key={item.label} className={`flex items-center gap-2 rounded-lg px-2 py-[5px] text-[12px] ${isActive ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-500'}`} data-id="element-217" data-mp="s9g43xblgxi8">
              <item.icon size={11} strokeWidth={1.9} className={`shrink-0 ${isActive ? 'text-[#2B211A]' : 'text-neutral-300'}`} data-id="element-218" data-mp="126kvcbkarj5w" />
              <span className="truncate" data-id="element-219" data-mp="167wepzckv85n">{item.label}</span>
            </li>;
      })}
      </ul>
    </>;
}